import type { Extension, Identifier } from "@medplum/fhirtypes";
import { encodeCatalogValue } from "../fhir/catalog-value";
import { deriveDeviceDefinitionFields } from "../fhir/device-definition-derived";
import {
  DEVICE_AVAILABILITY_URL,
  DEVICE_DEFINITION_LINEAGE_SYSTEM,
  DEVICE_DEFINITION_REVISION_SYSTEM,
  DEVICE_MEMBER_MARKS_URL,
  DEVICE_MINTED_AT_URL,
  DEVICE_PARAMETER_URL,
} from "../fhir/device-definition-storage";
import type { DeviceDefinitionRow } from "../fhir/types";
import type { DeviceDefinitionDto } from "../views/device-definition";

// ============================================================
// The optimistic row for a device kind save
//
// It must emit exactly what `device-definition.codec.ts` emits, because the
// authoritative row replaces this one and any difference reads as a flicker.
// NO DTO IS SMUGGLED ONTO THE ROW: the optimistic write builds a genuine FHIR
// `DeviceDefinition` and the projection reads it back through exactly the path
// a synced row takes — one projection, not two.
// ============================================================

/** Every extension url this writer claims. Anything else on the prior row belongs to another writer and survives. */
const OWNED_EXTENSIONS = new Set<string>([
  DEVICE_MINTED_AT_URL,
  DEVICE_AVAILABILITY_URL,
  DEVICE_MEMBER_MARKS_URL,
  DEVICE_PARAMETER_URL,
]);

function optimisticId(definitionUrl: string, version: number): string {
  let hash = 2166136261;
  for (const character of definitionUrl) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return `optimistic-device-kind-${(hash >>> 0).toString(36)}-${version}`;
}

/**
 * A provisional identity for a member the server has not minted yet. Positional
 * rather than sequenced: the row it lands on is replaced by the refetch, so this
 * only has to be stable enough for the DTO to render once.
 */
export function memberId(id: string | undefined, prefix: string, index: number): string {
  return id ?? `${prefix}-${index + 1}`;
}

/** The highest number among ids already present, so a reprojection never lowers a stored mark. */
function mark(ids: readonly string[]): number {
  return ids.reduce((high, id) => Math.max(high, Number(id.split("-").at(-1)) || 0), 0);
}

function identifiers(dto: DeviceDefinitionDto, prior: DeviceDefinitionRow | undefined): Identifier[] {
  const foreign = (prior?.identifier ?? []).filter(
    (identifier) =>
      identifier.system !== DEVICE_DEFINITION_LINEAGE_SYSTEM && identifier.system !== DEVICE_DEFINITION_REVISION_SYSTEM,
  );
  return [
    ...foreign,
    { system: DEVICE_DEFINITION_LINEAGE_SYSTEM, value: dto.definitionUrl },
    { system: DEVICE_DEFINITION_REVISION_SYSTEM, value: String(dto.version) },
  ];
}

/**
 * Rebuilt on the stored notes, not from scratch: changed text replaces the
 * first note and carries every later one across, and a cleared description
 * emits no note at all rather than an empty one.
 */
function note(dto: DeviceDefinitionDto, prior: DeviceDefinitionRow | undefined) {
  if (!dto.description) return undefined;
  const [first, ...rest] = prior?.note ?? [];
  if (first?.text === dto.description) return prior?.note;
  return [{ text: dto.description }, ...rest];
}

export function optimisticDeviceDefinitionRow(
  dto: DeviceDefinitionDto,
  prior?: DeviceDefinitionRow,
): DeviceDefinitionRow {
  let optionIndex = 0;
  const parameters = dto.parameters.map((parameter, index) => ({
    id: memberId(parameter.id, "p", index),
    label: parameter.label,
    value:
      parameter.value.kind === "Choice"
        ? {
            ...parameter.value,
            options: parameter.value.options.map((option) => ({
              id: memberId(option.id, "o", optionIndex++),
              label: option.label,
            })),
          }
        : { ...parameter.value },
  }));
  const optionIds = parameters.flatMap((parameter) =>
    parameter.value.kind === "Choice" ? parameter.value.options.map((option) => option.id) : [],
  );

  const foreign = (prior?.extension ?? []).filter((candidate) => !OWNED_EXTENSIONS.has(candidate.url));
  const extension: Extension[] = [
    ...foreign,
    { url: DEVICE_MINTED_AT_URL, valueDateTime: dto.mintedAt },
    { url: DEVICE_AVAILABILITY_URL, valueCode: dto.availability },
    {
      url: DEVICE_MEMBER_MARKS_URL,
      extension: [
        { url: "parameters", valueInteger: mark(parameters.map((parameter) => parameter.id)) },
        { url: "options", valueInteger: mark(optionIds) },
      ],
    },
    ...parameters.map(
      (parameter): Extension => ({
        url: DEVICE_PARAMETER_URL,
        extension: [
          { url: "memberId", valueString: parameter.id },
          { url: "label", valueString: parameter.label },
          encodeCatalogValue(parameter.value),
        ],
      }),
    ),
  ];

  const notes = note(dto, prior);
  const resource = {
    resourceType: "DeviceDefinition" as const,
    identifier: identifiers(dto, prior),
    deviceName: [{ name: dto.name, type: "model-name" as const }],
    ...(dto.manufacturer === undefined ? {} : { manufacturerString: dto.manufacturer }),
    ...(dto.modelNumber === undefined ? {} : { modelNumber: dto.modelNumber }),
    ...(notes === undefined ? {} : { note: notes }),
    extension,
  };
  return {
    ...resource,
    id: prior?.id ?? optimisticId(dto.definitionUrl, dto.version),
    ...deriveDeviceDefinitionFields(resource),
  };
}
